export const DEFAULT_PUBLIC_LANG = "en"
export const DEFAULT_CONTENTFUL_LOCALE = "en-US"

export function toContentfulLocale(publicLang: string): string {
  if (!publicLang) return DEFAULT_CONTENTFUL_LOCALE
  if (publicLang.toLowerCase() === DEFAULT_PUBLIC_LANG) {
    return DEFAULT_CONTENTFUL_LOCALE
  }
  try {
    return new Intl.Locale(publicLang).toString() // normalize the locale to the contentful locale so ru-ru returns as ru-RU, ja-jp returns as ja-JP, etc. This fixes the 5 broken locales 
  } catch {
    return publicLang
  }
}

/**
 * MXP and RC Contentful spaces can use different locale codes for the same
 * language. For example, corporate's MXP space uses `de` while its RC space
 * uses `de-DE`.
 *
 * Preferred resolution order:
 *   1. `rcLocaleCode` on the matching Language entry in the site config
 *      (set per-site by marketers via the "RC Language Code" field).
 *   2. The legacy hardcoded overrides below (kept as a backstop while spaces
 *      are being backfilled — remove once all Language entries have their
 *      `rcLocaleCode` populated where needed).
 *   3. `toContentfulLocale(publicLang)`.
 */
const PUBLIC_LANG_TO_RC_LOCALE_OVERRIDES: Record<string, string> = {
  de: "de-DE",
}

type SiteConfigLike = {
  siteLanguagesCollection?: {
    items?: Array<{
      code?: string | null
      rcLocaleCode?: string | null
    } | null> | null
  } | null
}

export function toRcContentfulLocale(
  publicLang: string,
  siteConfig?: SiteConfigLike | null,
): string {
  if (!publicLang) return DEFAULT_CONTENTFUL_LOCALE

  const items = siteConfig?.siteLanguagesCollection?.items ?? []
  const match = items.find(
    l => l?.code?.toLowerCase() === publicLang.toLowerCase(),
  )
  if (match?.rcLocaleCode) return match.rcLocaleCode

  const override = PUBLIC_LANG_TO_RC_LOCALE_OVERRIDES[publicLang.toLowerCase()]
  if (override) return override
  return toContentfulLocale(publicLang)
}
